import { Link } from 'react-router-dom'
import { Card } from './Card'
import { RoomThumb } from './RoomThumb'
import { useHubLang } from '../lib/i18n'
import type { HubLang } from '../lib/i18n'
import { CONTESTS } from '../lib/hubCopy'
import { dayLabel, monthLabel, type Contest } from '../lib/photoContests'

/**
 * 147 (CMP-6). The contest card on the hub — a HOOK since 11 Oct.
 *
 * ── IN THE RAIL, AND IN THE PHONE'S SWIPE ROW (David, 11 Oct) ───────────────
 * It sat in the main column from 10 Oct because the rail did not render below
 * 820px (SHL-18) and the photographers are on phones. On 11 Oct the rail
 * stopped disappearing: below 1100px it is the swipe row ABOVE the feed
 * (hub.css, `.hub__hooks`), so the card can live with the other widgets and
 * still be on every phone — and the feed is kept clean, which was the ask.
 *
 * ── THE CARD IS THE HOOK, THE CONTEST PAGE IS THE DETAIL ────────────────────
 * Name, a strip of photos, where the contest is, the totals and the one thing
 * to do now. The objective, the prize, the judges' award and your votes left
 * are on #/contests/:id, one tap away — "the widget is just the hook".
 *
 * ── WHICH CONTEST, AND WHEN ─────────────────────────────────────────────────
 * The server decides (photo_contest_list.on_hub, and its order): a themed
 * contest while entries, votes or the judges' confirmation are open; the
 * monthly contest only while voting or being confirmed — it is always taking
 * entries, so a card for that would be a card forever; any contest for a week
 * after its results are confirmed. One card: the first the server ranks.
 *
 * Counts are aggregate only (rule 8): photos and total votes, never a photo's.
 */
export function ContestCard({ contest }: { contest: Contest }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const c = contest
  const cta = ctaFor(c, lang)
  const final = c.phase === 'final' && c.winners.length > 0
  const top = final ? c.winners.find((w) => w.award === 'place') ?? c.winners[0] : null
  const strip = final ? c.winners.map((w) => w.path) : c.thumbs
  return (
    <Card
      className="contestcard hook"
      title={
        <span className="contestcard__title">
          <span className="contestcard__eyebrow">{t.eyebrow}</span>
          {nameOf(c, lang)}
        </span>
      }
    >
      {strip.length > 0 && (
        <ul className="contestcard__thumbs" aria-hidden="true">
          {strip.slice(0, 4).map((p) => (
            <li key={p}>
              <RoomThumb path={p} className="contestcard__thumb" />
            </li>
          ))}
        </ul>
      )}
      {top ? (
        <p className="contestcard__when">
          <strong>{top.award === 'judges' ? t.judgesAward(top.rank, false) : t.placeName(top.rank)}</strong>
          {' — '}
          {top.name}
        </p>
      ) : (
        <p className="contestcard__when">{phaseLine(c, lang)}</p>
      )}
      <p className="hook__meta">{t.counts(c.entryCount, c.kind === 'vote' ? c.voteTotal : null)}</p>
      <Link className={`btn btn--sm btn--block ${final ? 'btn--quiet' : 'btn--primary'}`} to={`/contests/${c.id}`}>
        {cta}
      </Link>
    </Card>
  )
}

/** The contest's name: the monthly one says which month. */
export function nameOf(c: Contest, lang: HubLang): string {
  return c.periodMonth ? CONTESTS[lang].monthlyName(c.title, monthLabel(c.periodMonth, lang)) : c.title
}

/** One sentence for where a contest is — the server's phase, the contest's dates. */
export function phaseLine(c: Contest, lang: HubLang): string {
  const t = CONTESTS[lang]
  const day = (d: string) => dayLabel(d, lang)
  const next = (d: string) => {
    const [y, m, dd] = d.split('-').map(Number)
    const n = new Date(Date.UTC(y, m - 1, dd + 1))
    return n.toISOString().slice(0, 10)
  }
  switch (c.phase) {
    case 'upcoming':
      return t.upcoming(day(c.startsOn))
    case 'entering':
      return c.seriesId ? t.enteringMonthly(day(next(c.endsOn))) : t.entering(day(c.endsOn))
    case 'voting':
      return t.voting(day(c.voteEndsOn ?? c.endsOn))
    case 'confirming':
      return c.kind === 'vote' ? t.confirmingVote : t.confirmingJudged
    case 'final':
      return t.final(c.judgedAt ? day(c.judgedAt.slice(0, 10)) : '')
    default:
      return t.cancelled
  }
}

/** What the card's button says: the one thing the reader can do now. */
export function ctaFor(c: Contest, lang: HubLang): string {
  const t = CONTESTS[lang]
  if (c.phase === 'final') return t.winnersCta
  if (c.phase === 'confirming') return c.isJudge ? t.confirmCta : t.photosCta
  if (c.phase === 'voting') return c.isJudge ? t.photosCta : t.voteCta
  if (c.phase === 'entering' && !c.seriesId) return t.enterCta
  return t.photosCta
}
